import { NavLink } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, TrendingUp, X } from 'lucide-react';
import { NAV_SECTIONS, SETTINGS_ITEM, isNavItemActive } from '@/layouts/nav';
import type { NavItem } from '@/layouts/nav';
import { cn } from '@/lib/cn';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  pathname: string;
}

function NavRow({
  item,
  collapsed,
  pathname,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  pathname: string;
  onNavigate: () => void;
}) {
  const active = isNavItemActive(item, pathname);
  return (
    <NavLink
      to={item.href}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      title={collapsed ? item.label : undefined}
      className={cn(
        'relative flex h-9 items-center gap-2.5 rounded-lg text-sm font-medium transition-colors',
        collapsed ? 'justify-center px-0' : 'px-2.5',
        active ? 'bg-surface-2 text-fg' : 'text-fg-subtle hover:bg-surface-2 hover:text-fg-muted'
      )}
    >
      {active && <span className="absolute top-2 bottom-2 -left-2 w-0.5 rounded-full bg-brand" aria-hidden="true" />}
      <item.icon
        className={cn('h-4 w-4 shrink-0', active ? 'text-brand-fg' : 'text-fg-faint')}
        aria-hidden="true"
      />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
}

export function Sidebar({ collapsed, onToggleCollapsed, mobileOpen, onCloseMobile, pathname }: SidebarProps) {
  return (
    <aside
      id="app-sidebar"
      aria-label="Main navigation"
      className={cn(
        'fixed inset-y-0 left-0 z-50 flex h-full w-64 shrink-0 flex-col border-r border-line bg-surface transition-transform duration-200 lg:static lg:translate-x-0',
        collapsed ? 'lg:w-16' : 'lg:w-60',
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      )}
    >
      {/* Brand */}
      <div
        className={cn(
          'flex h-14 shrink-0 items-center border-b border-line',
          collapsed ? 'justify-center px-2' : 'gap-2.5 px-4'
        )}
      >
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand">
          <TrendingUp className="h-4 w-4 text-white" aria-hidden="true" />
        </div>
        {!collapsed && <span className="truncate text-sm font-semibold tracking-tight text-fg">TradePlatform</span>}
        <button
          type="button"
          onClick={onCloseMobile}
          aria-label="Close navigation"
          className="ml-auto rounded-md p-1.5 text-fg-subtle hover:bg-surface-2 hover:text-fg lg:hidden"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/* Sections */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {NAV_SECTIONS.map((section) => (
          <div key={section.title} className="mb-4 last:mb-0">
            {!collapsed && (
              <p className="px-2.5 pb-1.5 text-[11px] font-medium tracking-wider text-fg-faint uppercase">
                {section.title}
              </p>
            )}
            <div className="flex flex-col gap-0.5">
              {section.items.map((item) => (
                <NavRow
                  key={item.href}
                  item={item}
                  collapsed={collapsed}
                  pathname={pathname}
                  onNavigate={onCloseMobile}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer: settings + collapse */}
      <div className="shrink-0 border-t border-line px-2 py-2">
        <NavRow item={SETTINGS_ITEM} collapsed={collapsed} pathname={pathname} onNavigate={onCloseMobile} />
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={cn(
            'mt-0.5 hidden h-9 items-center gap-2.5 rounded-lg text-sm font-medium text-fg-subtle transition-colors hover:bg-surface-2 hover:text-fg-muted lg:flex',
            collapsed ? 'w-full justify-center px-0' : 'w-full px-2.5'
          )}
        >
          {collapsed ? (
            <PanelLeftOpen className="h-4 w-4 shrink-0 text-fg-faint" aria-hidden="true" />
          ) : (
            <>
              <PanelLeftClose className="h-4 w-4 shrink-0 text-fg-faint" aria-hidden="true" />
              <span>Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
